import { RiCloseCircleLine, RiTeamLine, RiUserAddLine } from "@remixicon/react";
import React, {
    ReactElement,
    useMemo,
} from "react";
import { MdGroup } from "react-icons/md";
import {
    AssignmentType, GetQueueDocument,
    GetQueuesDocument,
    HandleType,
    QueueMember,
    QueueMemberInput,
    QueueMemberRole,
    TransferSettingsDocument,
    useGetQueueQuery,
    User,
    UserGroup,
    useSaveDefaultQueueMutation,
    useSaveQueueMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import Avatar from "../../../../components/avatar/Avatar";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../components/expanding-section/AdvancedSection";
import AgentPicker, { AgentOrGroup } from "../../../../components/pickers/agents/Agents";
import ChannelTags from "../../../../components/pickers/channel-tags/ChannelTags";
import EmailAliases from "../../../../components/pickers/email-aliases/EmailAliases";
import Select, { Option } from "../../../../components/select/Select";
import Badge from "../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import Toggle from "../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../contexts/HelpContext";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import useRouting, { Link, Paths, useParams } from "../../../../contexts/Routing";
import { ErrorRemote } from "../../../error/Error";
import Styles from "./Queues.module.scss";
import QueueTable from "./QueueTable";

const ROUTING_OPTIONS: Option[] = [
    {
        label: "None",
        value: AssignmentType.None,
    },
    {
        label: "Round Robin",
        value: AssignmentType.RoundRobin,
    },
];

const ROLE_TO_NAME = {
    [QueueMemberRole.Assignee]: "Assignee",
    [QueueMemberRole.Observer]: "Observer",
};

const ROLE_OPTIONS = [
    {
        label: ROLE_TO_NAME[QueueMemberRole.Assignee],
        value: QueueMemberRole.Assignee,
    },
    {
        label: ROLE_TO_NAME[QueueMemberRole.Observer],
        value: QueueMemberRole.Observer,
    },
];

function RolePicker({
    value,
    onChange,
}: {
    value: QueueMemberRole;
    onChange(value: QueueMemberRole): void;
}) {
    return (
        <Select
            className=""
            minRequired={1}
            options={ROLE_OPTIONS}
            value={{
                label: ROLE_TO_NAME[value],
                value,
            }}
            onChange={role => onChange(role.value as QueueMemberRole)}
        />
    );
}

interface QueueMemberWithEmail extends QueueMember {
    id: string;
    email?: string;
}
const TableContainer = createTableContainer<QueueMemberWithEmail>();

export function useViewMember({
    canSelectRole,
    members,
    onChange,
}: {
    canSelectRole: boolean;
    members: QueueMemberInput[];
    onChange: (members: QueueMemberInput[]) => void;

}) {
    const setMembersRole = async (addedMembers: AgentOrGroup[], role: QueueMemberRole) => {
        const updateSet = new Set(addedMembers.toIdList(row => row.data.id as string));
        let newSet = members.map(member => (updateSet.has(member.memberId) ? {
            ...member,
            role,
        } : member));

        newSet = newSet.concat(addedMembers
            .exclude(
                members.toIdList(current => (current.memberId ?? current.userId) as string),
                user => (typeof user === "string" ? user : user.data.id),
            )
            .map(current => ({
                role,
                memberId: current.data.id,
                userId: current.data.id,
                user: (current.type === "user" ? current.data : undefined) as User,
                group: (current.type === "group" ? current.data : undefined) as UserGroup,
            })));

        onChange(newSet);
    };

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Add Members",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            setMembersRole(formTracker.properties.members.value, formTracker.properties.role.value);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .array("members", () => [] as AgentOrGroup[])
                .property("role", () => QueueMemberRole.Assignee),
            dependencies: [],
        },
        body: tracker => (
            <>
                <FormGroup label="Members" helpText="Select the members you want to add to the queue." helpDisplay="icon">
                    <AgentPicker
                        shouldIncludeGroups
                        isMulti
                        value={tracker.properties.members.value.map(row => row.data.id)}
                        onSelected={(__, agents) => tracker.properties.members.setValue(agents)}
                    />
                </FormGroup>
                {canSelectRole && (
                    <FormGroup label="Role" helpText="What role would the members have." helpDisplay="icon">
                        <RolePicker value={tracker.properties.role.value} onChange={tracker.properties.role.setValue} />
                    </FormGroup>
                )}
            </>
        ),
    });

    return {
        element,
        setOpen,
    };
}

function UserManager({
    members,
    onChange,
    canSelectRole,
    isLoading,
}: {
    members: QueueMember[];
    onChange: (members: QueueMemberInput[]) => void;
    canSelectRole: boolean;
    isLoading: boolean;
}) {
    const { element: viewMemberElement, setOpen } = useViewMember({ canSelectRole, members, onChange });

    const deleteUser = async (id: string) => {
        onChange(members.filter(member => (member.userId !== id) && (member.memberId !== id)));
    };

    const users = useMemo<QueueMemberWithEmail[]>(() => members.map(member => ({
        ...member,
        id: member.memberId,
        email: member.user?.handles?.find(handle => handle.type === HandleType.Email)?.email?.email,
    })), [members]);

    return (
        <>
            {viewMemberElement}
            <TableContainer data={users as QueueMemberWithEmail[] ?? []} isLoading={isLoading} borderVariant="secondary">
                <TableContainer.Empty>
                    <TableContainer.Empty.Default
                        icon={<RiUserAddLine />}
                        title="Add Members to the Queue"
                        text="Streamlining task assignment and keeping the workflow organized"
                        buttonText="Add Members"
                        onClick={() => setOpen(true)}
                    />
                </TableContainer.Empty>
                <TableContainer.TableControls>

                    <TableContainer.TableControls.Search
                        search={(query, member) => member.user?.name?.toLocaleLowerCase().includes(query)
                            || member.group?.name?.toLocaleLowerCase().includes(query)
                            || member.role?.toLocaleLowerCase().includes(query)
                            || member.email?.toLocaleLowerCase().includes(query)}
                    />
                    <TableContainer.TableControls.Seperator />

                    <TableContainer.TableControls.Create
                        text="Add Member"
                        variant="outline-blue"
                        onClick={() => setOpen(true)}
                    />
                </TableContainer.TableControls>
                <TableContainer.Table>
                    <TableContainer.TableValue
                        columnKey="name"
                        title="User/Group Name"
                        sortBy={item => item.user?.name?.toLowerCase() || item.group?.name?.toLowerCase()}
                    >
                        {item => (
                            item.group ? (
                                <>
                                    <RiTeamLine className={Styles["rounded-icon"]} />
                                    {" "}
                                    {item.group.name}
                                </>
                            ) : (
                                <>
                                    <Avatar src={item.user?.avatarUrl as string} variant="xs" />
                                    {" "}
                                    {item.user?.name}
                                </>
                            )
                        )}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="email"
                        title="Email"
                        sortBy={item => item.email?.toLowerCase()}
                    >
                        {item => item.email || "-"}
                    </TableContainer.TableValue>
                    {canSelectRole && (
                        <TableContainer.TableValue
                            columnKey="role"
                            title="Role"
                            sortBy={item => item.role?.length}
                        >
                            {item => (
                                <RolePicker
                                    value={item.role}
                                    onChange={role => {
                                        const copy = [...members];
                                        const index = copy.findIndex(member => member.memberId === item.memberId);
                                        copy[index] = {
                                            ...copy[index],
                                            role,
                                        };
                                        onChange(copy);
                                    }}
                                />
                            )}
                        </TableContainer.TableValue>
                    )}

                    <TableContainer.Action
                        icon={<RiCloseCircleLine />}
                        altText="Delete members"
                        onClick={item => deleteUser(item.memberId)}
                    />

                </TableContainer.Table>

            </TableContainer>
        </>
    );
}

function DisplayQueue({ id }: { id: string; }): ReactElement {
    useArticleContext("urn:fq:article:8c9b738bc4234e6ca05aece49ebfafb5");

    const { historyPush } = useRouting();
    const { data, error, loading: isLoading } = useGetQueueQuery({
        skip: id === "new",
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });

    const [save] = useSaveQueueMutation();
    const [saveDefault] = useSaveDefaultQueueMutation();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("name", () => data?.queue?.name ?? "")
            .property("description", () => data?.queue?.description ?? "")
            .property("isDefault", () => data?.queue?.isDefault ?? false)
            .property("userAssignmentType", () => data?.queue?.userAssignmentType ?? AssignmentType.None)
            .object("members", () => data?.queue?.members.map(cloneWithoutTypename) ?? [])
            .array("channelTags", () => data?.queue?.channelTags ?? [])
            .array("emailAliasIds", () => data?.queue?.emailAliasIds ?? [])
            .array("integrationIds", () => data?.queue?.integrationIds ?? []),
        [data?.queue],
    );

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Queue settings saved.",
        onSave: (__, onDone) => {
            LoggingHandler.instance.wrap(async () => {
                const savedItem = await save({
                    refetchQueries: [
                        { query: GetQueueDocument },
                        { query: GetQueuesDocument },
                        { query: TransferSettingsDocument },
                    ],
                    variables: {
                        id: id !== "new" ? id : undefined,
                        queue: {
                            name: properties.name.value,
                            description: properties.description.value,
                            members: properties.members.value.map(member => ({
                                role: member.role,
                                userId: member.userId,
                                memberId: member.memberId ?? member.userId,
                            })),
                            channelTags: properties.channelTags.value,
                            emailAliasIds: properties.emailAliasIds.value,
                            integrationIds: properties.integrationIds.value,
                            userAssignmentType: properties.userAssignmentType.value,
                        },
                    },
                });

                if (properties.isDefault.isDirty) {
                    await saveDefault({
                        variables: {
                            id: properties.isDefault.value
                                ? savedItem.data?.saveQueue.id
                                : undefined,
                        },
                    });
                }
                if (id === "new") {
                    historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                        setting: "queues",
                        id: savedItem.data?.saveQueue.id,
                    });
                }

                onDone();
            });
        },
    });

    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <SettingsContainer saveElement={saveElement}>
            <SettingsContainer.Section
                title="Details"
                helpText="These general are the general settings required to create and identify the queue. "
            >
                <SettingsContainer.Setting
                    title="Queue Name"
                    description="The name of the queue"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        placeholder="Helpdesk Team"
                        required
                        onChange={evt => properties.name.setValue(evt.target.value)}
                        value={properties.name.value}
                        size="small"
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Queue Description"
                    description="Any information you want to leave to help you understand what this queue is created for."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        display="textarea"
                        placeholder="This queue is used for..."
                        onChange={evt => properties.description.setValue(evt.target.value)}
                        value={properties.description.value}
                        size="small"
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Default Queue"
                    description="Tickets should be assigned to this queue if they are not
                        assigned to any other queue or part of any other channel"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <Toggle
                        checked={properties.isDefault.value}
                        onChange={event => properties.isDefault.setValue(event.target.checked)}
                    />
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
            <SettingsContainer.Section
                title="Routing"
                helpText="This section helps manage how tickets will be assigned to queues when they are
                    created from different sources such as Slack and email."
            >
                <SettingsContainer.Setting
                    title="Routing"
                    description="Do you want to automatically route requests to individual users?"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <Select
                        placeholder="None"
                        options={ROUTING_OPTIONS}
                        value={!properties.userAssignmentType.value ? undefined
                            : ROUTING_OPTIONS.find(option => option.value === properties.userAssignmentType.value)}
                        onChange={value => properties.userAssignmentType.setValue(value.value as AssignmentType)}
                        size="small"
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Channel Groups"
                    isLoading={isLoading}
                    description="The easiest way to assign tickets to a queue is by associating
                    a queues and channels with channel groups. By adding a channel group here,
                    any ticket created in a channel associated with the group will be automatically
                    assigned to this queue. To manage channel Groups, visit Channel Group Settings"
                    descriptionElement={(
                        <>
                            The easiest way to assign tickets to a queue is by associating
                            a queues and channels with channel groups. By adding a channel group here,
                            any ticket created in a channel associated with the group will be automatically
                            assigned to this queue. To manage channel Groups, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-tags" }}
                            >
                                Channel Group Settings
                            </Link>
                            .
                        </>
                    )}
                    containerClassName={Styles["channel-no-border"]}
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                >
                    <ChannelTags
                        isMulti
                        placeholder="Select Channel Groups"
                        value={properties.channelTags.value}
                        onSelected={properties.channelTags.setValue}
                    />
                </SettingsContainer.Setting>
                <AdvancedSection title="Advanced Settings">
                    <SettingsContainer.Setting
                        title="Email Aliases"
                        isLoading={isLoading}
                        description="Configure email aliases to automatically route to the current queue
                                    by configuring which aliases should route to the queue in the above
                                    list. To configure more email aliases, view Email Channel Settings."
                        descriptionElement={(
                            <>
                                Configure email aliases to automatically route to the current queue
                                by configuring which aliases should route to the queue in the above
                                list. To configure more email aliases, view
                                {" "}
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING}
                                    params={{ setting: "channels-emails" }}
                                >
                                    Email Channel settings
                                </Link>
                                .
                            </>
                        )}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <EmailAliases
                            placeholder="Please pick the email alias"
                            isMulti
                            onSelected={properties.emailAliasIds.setValue}
                            value={properties.emailAliasIds.value}
                        />
                    </SettingsContainer.Setting>
                </AdvancedSection>
            </SettingsContainer.Section>
            <SettingsContainer.Section
                title="Members"
                helpText="The list below lets you manage the members of the queue. You can either directly add users or
                    groups to the queue. If you add a group, all users in the group will be added to the queue. This is the
                    recommended approach for most people. Keep in mind, if a member is part of multiple groups, it might start
                    to be confusing which users are part of the queue and which are not."
            >
                <SettingsContainer.Setting
                    title="Members"
                    description="Do you want to automatically route requests to individual users?"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                >

                    <UserManager
                        members={properties.members.value as QueueMember[]}
                        onChange={properties.members.setValue}
                        canSelectRole={properties.userAssignmentType.value !== AssignmentType.None}
                        isLoading={isLoading}
                    />
                </SettingsContainer.Setting>
            </SettingsContainer.Section>

        </SettingsContainer>

    );
}

export default function QueueSettings(): ReactElement {
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? <DisplayQueue id={id} /> : <QueueTable />;
}
